import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useProviderStore } from "core/store/providerStore";
import { invalidateFleetQueries } from "core/query/invalidateFleet";
import { useAuthStore } from "core/store/authStore";
import { createOrganization } from "features/organizations/services/organizationApi";
import { createVehicle } from "features/vehicles/services/vehicleApi";
import { ROUTES } from "core/router/routes";
import type { VehicleType } from "types/api.generated";
import { formatVehicleType } from "features/drivers/lib/driverLicenceStatus";

const VEHICLE_TYPES: { value: VehicleType; label: string }[] = [
  { value: "bike", label: "Bike" },
  { value: "tricycle", label: "Tricycle" },
  { value: "car", label: "Car" },
  { value: "van", label: "Van" },
  { value: "truck", label: "Truck" },
];

export default function ProviderSetup() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    orgMemberships,
    activeOrgId,
    setActiveOrgId,
    setActiveVehicleId,
    refreshVehicles,
  } = useProviderStore();
  const { hydrateFromMe } = useAuthStore();

  const hasOrg = orgMemberships.length > 0;
  const [orgName, setOrgName] = useState("");
  const [vehicleType, setVehicleType] = useState<VehicleType>("bike");
  const [plateNumber, setPlateNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      let organizationId = activeOrgId ?? orgMemberships[0]?.orgId ?? null;

      if (!hasOrg) {
        if (!orgName.trim()) {
          setError("Organisation name is required.");
          setLoading(false);
          return;
        }
        const org = await createOrganization({ name: orgName.trim() });
        organizationId = org.id;
        setActiveOrgId(org.id);
        await hydrateFromMe();
      }

      if (!organizationId) {
        setError("No organisation found. Please try again.");
        setLoading(false);
        return;
      }

      if (!plateNumber.trim()) {
        setError("Plate number is required.");
        setLoading(false);
        return;
      }

      const vehicle = await createVehicle({
        organizationId,
        vehicleType,
        plateNumber: plateNumber.trim(),
        label: `${formatVehicleType(vehicleType)} · ${plateNumber.trim()}`,
      });

      setActiveVehicleId(vehicle.id);
      await invalidateFleetQueries(queryClient, {
        orgId: organizationId,
        vehicleId: vehicle.id,
      });
      await refreshVehicles({ force: true });
      navigate(`${ROUTES.PROVIDER.DASHBOARD}?vehicle=${vehicle.id}`, {
        replace: true,
      });
    } catch (err: any) {
      setError(err?.message || "Setup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 font-display flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
        <h1 className="text-2xl font-black text-slate-900 mb-1">
          Provider setup
        </h1>
        <p className="text-sm text-slate-500 mb-6">
          {hasOrg
            ? "Add your first vehicle to get started."
            : "Create your organisation and register your first vehicle."}
        </p>

        <form onSubmit={onSubmit} className="space-y-4">
          {!hasOrg && (
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">
                Organisation name
              </label>
              <input
                type="text"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="My Delivery Co."
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Vehicle type
            </label>
            <select
              value={vehicleType}
              onChange={(e) => setVehicleType(e.target.value as VehicleType)}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
            >
              {VEHICLE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-1">
              Plate number
            </label>
            <input
              type="text"
              value={plateNumber}
              onChange={(e) => setPlateNumber(e.target.value)}
              className="w-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              placeholder="ABC-123-XY"
            />
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white font-bold py-3 rounded-xl hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {loading ? "Saving…" : "Continue"}
          </button>
        </form>
      </div>
    </div>
  );
}
